import React from "react";
import moment from "moment";
import qs from "qs";
import { Grid, Tab, Tabs } from "@mui/material";
import { getAnalytic } from "http/infoApi";

// React components
import SoftBox from "components/SoftBox";

// React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";

// Data
import { useQuery } from "react-query";
import {
  calculatePercentageChange,
  filterAndFormat,
  getLast30DaysDates,
  generateChartData,
  getDate,
  // getCurrentMonthDates,
} from "layouts/dashboard/helper/chart-helper";

// i18n
import { useTranslation } from "react-i18next";

const Dashboard = () => {
  const { t } = useTranslation();
  const [sortByTime, setSortByTime] = React.useState("day");
  const [status, setStatus] = React.useState(0);
  const [currentTimezone, setCurrentTimezone] = React.useState(localStorage.getItem("timezone"));

  // Use translation keys for status tabs
  const statusTabs = [
    { key: "All", label: t("status.all") },
    { key: "Completed", label: t("status.completed") },
    { key: "NmtCheck", label: t("status.nmtcheck") },
    { key: "Failed", label: t("status.failed") },
  ];

  const [value, setValue] = React.useState(0);
  const handleChange = (__, newValue) => {
    setValue(newValue);
  };
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  React.useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "timezone") {
        setCurrentTimezone(e.newValue);
      }
    };

    // Listen for storage events (works when localStorage is changed from other tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    // Custom event listener for same-tab localStorage changes
    const handleCustomStorageChange = (e) => {
      if (e.detail.key === "timezone") {
        setCurrentTimezone(e.detail.newValue);
      }
    };

    window.addEventListener("localStorageChanged", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageChanged", handleCustomStorageChange);
    };
  }, []);

  const { data: analytic } = useQuery(["analytic", sortByTime, currentTimezone], () => {
    return getAnalytic(
      qs.stringify({
        time: sortByTime,
        timezone: currentTimezone || userTimezone,
      })
    );
  });

  const getTime = (data, dates) => {
    return dates.map((date) => {
      const timezone = localStorage.getItem("timezone") || "UTC";
      // ИСПРАВЛЕНО: создаем targetDate с учетом timezone
      const targetDate = moment.tz(date, timezone).valueOf();

      let type =
        sortByTime === "day"
          ? "hour"
          : sortByTime === "month"
          ? "day"
          : sortByTime === "year"
          ? "month"
          : sortByTime === "all"
          ? "month"
          : "day";

      const dataPoint = data?.find((item) => {
        if (!item[type]) return false;
        // Обрабатываем данные от сервера с timezone
        return moment.tz(item[type], timezone).valueOf() === targetDate;
      });

      return dataPoint ? dataPoint?.totalAmount : 0;
    });
  };

  const transactionsLineChartData = {
    labels: getDate(sortByTime, analytic?.transaction.firstDate),
    datasets: [
      (statusTabs?.[status]?.key === "Completed" || statusTabs?.[status]?.key === "All") && {
        label: t("status.completed"),
        color: "success",
        data: getTime(
          analytic?.transaction?.completedData,
          getDate(sortByTime, analytic?.transaction.firstDate)
        ),
      },
      (statusTabs?.[status]?.key === "NmtCheck" || statusTabs?.[status]?.key === "All") && {
        label: t("status.nmtcheck"),
        color: "warning",
        data: getTime(
          analytic?.transaction?.pendingData,
          getDate(sortByTime, analytic?.transaction.firstDate)
        ),
      },
      (statusTabs?.[status]?.key === "Failed" || statusTabs?.[status]?.key === "All") && {
        label: t("status.failed"),
        color: "error",
        data: getTime(
          analytic?.transaction?.failedData,
          getDate(sortByTime, analytic?.transaction.firstDate)
        ),
      },
    ].filter(Boolean), // Filters out any falsey values (undefined or null datasets)
  };

  console.log(analytic, "analytic");
  const transactionRes = analytic?.transaction?.transactionRes?.data?.[0];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: t("dashboard.transacted_amount_today") }}
                count={transactionRes?.totalAmountForDay}
                currency={true}
                percentage={{ color: "success" }}
                icon={{ color: "warning", component: "payment_icon" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                currency={true}
                title={{ text: t("dashboard.total_transacted_amount") }}
                count={transactionRes?.totalAmount}
                percentage={{ color: "success" }}
                icon={{ color: "warning", component: "payment_icon" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: t("dashboard.total_transactions") }}
                count={transactionRes?.completedCount}
                percentage={{ color: "error" }}
                icon={{ color: "success", component: "emoji_events" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: t("dashboard.users") }}
                count={analytic?.user}
                percentage={{ color: "success" }}
                icon={{
                  color: "error",
                  component: "people_icon",
                }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        <SoftBox mb={3}>
          <Grid container spacing={1}>
            <Grid item xs={12} lg={3}>
              <ReportsBarChart
                color="success"
                title={t("dashboard.successful_transactions")}
                description={
                  calculatePercentageChange(
                    analytic?.transaction?.all?.completedData,
                    analytic?.transaction?.all
                  ) || ""
                }
                date={t("dashboard.just_updated")}
                chart={generateChartData({
                  data: analytic,
                  label: "Successful Tran (%)",
                  color: "success",
                  renderKey: "completedData",
                  type: sortByTime,
                })}
              />
            </Grid>
            <Grid item xs={12} lg={3}>
              <ReportsBarChart
                color="success"
                title={t("dashboard.transacted_amount")}
                description={
                  calculatePercentageChange(
                    analytic?.transaction?.all?.successTransacted,
                    analytic?.transaction?.all,
                    "successTransacted"
                  ) || ""
                }
                date={t("dashboard.just_updated")}
                chart={generateChartData({
                  data: analytic,
                  label: "Complited  Tran (%)",
                  color: "success",
                  renderKey: "successTransacted",
                  type: sortByTime,
                })}
              />
            </Grid>
            <Grid item xs={12} lg={3}>
              <ReportsBarChart
                color="warning"
                title={t("dashboard.nmtcheck_transactions")}
                description={
                  calculatePercentageChange(
                    analytic?.transaction?.all?.pendingData,
                    analytic?.transaction?.all
                  ) || ""
                }
                date={t("dashboard.just_updated")}
                chart={generateChartData({
                  data: analytic,
                  label: "NmtCheck  Tran (%)",
                  color: "warning",
                  renderKey: "pendingData",
                  type: sortByTime,
                })}
              />
            </Grid>
            <Grid item xs={12} lg={3}>
              <ReportsBarChart
                color="error"
                title={t("dashboard.failed_transactions")}
                description={
                  calculatePercentageChange(
                    analytic?.transaction?.all?.failedData,
                    analytic?.transaction?.all
                  ) || ""
                }
                date={t("dashboard.just_updated")}
                chart={generateChartData({
                  data: analytic,
                  label: "Failed  Tran (%)",
                  color: "error",
                  renderKey: "failedData",
                  type: sortByTime,
                })}
              />
            </Grid>
          </Grid>
        </SoftBox>

        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={12}>
              <GradientLineChart
                title={t("dashboard.volume_transactions")}
                filters={
                  <SoftBox display="flex" alignItems="center" justifyContent="space-between" pb={2}>
                    <Tabs value={status} onChange={(___, newValue) => setStatus(newValue)}>
                      {statusTabs.map((tab, index) => (
                        <Tab key={index} label={tab.label} disableRipple />
                      ))}
                    </Tabs>

                    <Tabs onChange={handleChange} value={value} color="warning">
                      <Tab onClick={() => setSortByTime("day")} label={t("time.day")} />
                      <Tab onClick={() => setSortByTime("week")} label={t("time.days_7")} />
                      <Tab onClick={() => setSortByTime("month")} label={t("time.days_30")} />
                      <Tab onClick={() => setSortByTime("year")} label={t("time.year")} />
                      <Tab onClick={() => setSortByTime("all")} label={t("time.all")} />
                    </Tabs>
                  </SoftBox>
                }
                height="auto"
                chart={transactionsLineChartData}
              />
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
    </DashboardLayout>
  );
};

export default Dashboard;
